import { get } from 'lodash';
import { logger } from '@fiverr-private/obs';
import { BuilderCMSResponse, MetricEvent, RelatedSkill, SkillInfo } from '../shared/types';
import { countEventError, countEventSuccess, logEventErrorTime, logEventSuccessTime } from '../events/countEvent';

export const getSkillsTranslations = async ({ locale }: { locale?: string } = {}) => {
    try {
        if (!locale) {
            return {};
        }
        const { structured_skills } = i18n?.translations?.[locale] || {};

        return { structured_skills };
    } catch (error) {
        return {};
    }
};

export const trackRequestMetrics =
    <T>() =>
    async (promise: Promise<T>, name: MetricEvent): Promise<any> => {
        const fetchStartTime = new Date();

        try {
            const data = await promise;
            countEventSuccess({ type: name });
            logEventSuccessTime({ type: name, time: fetchStartTime });

            return data;
        } catch (error) {
            const status = get(error, 'response.status');
            countEventError({ type: name, code: status });
            logEventErrorTime({ type: name, code: status, time: fetchStartTime });
            throw error;
        }
    };

interface translatedSkillNameParams {
    skillId: string;
    skillSlug: string;
    locale: string;
}

export const translateSkillName = ({ skillId, skillSlug, locale }: translatedSkillNameParams): string | undefined => {
    const aliasPluralKey = `structured_skills.${skillId}.profession_plural_alias`;
    const aliasKey = `structured_skills.${skillId}.profession_alias`;
    const nameKey = `structured_skills.${skillId}.name`;
    const ugcSkillKey = `ugc_skills.${skillSlug}.name`;

    const hasTranslation = (key: string) => i18n.has(key, { $scope: locale });
    const getTranslation = (key: string) => i18n.t(key, { $scope: locale });

    if (hasTranslation(aliasPluralKey)) {
        return getTranslation(aliasPluralKey);
    } else if (hasTranslation(aliasKey)) {
        return getTranslation(aliasKey);
    } else if (hasTranslation(nameKey)) {
        return getTranslation(nameKey);
    } else if (hasTranslation(ugcSkillKey)) {
        return getTranslation(ugcSkillKey);
    }
};

interface DataInfo {
    month: string;
    year: string;
}

const getDataInfo = (): DataInfo => {
    const date = new Date();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear().toString();
    return { month, year };
};

interface HydrateTemplateWithVariableParams {
    template: string;
    variables: Record<string, string>;
}

export const hydrateTemplate = ({ template = '', variables }: HydrateTemplateWithVariableParams): string => {
    let hydratedTemplate = template;
    for (const [key, value] of Object.entries(variables)) {
        const regex = new RegExp(`\\$\\{${key}\\}`, 'g');
        hydratedTemplate = hydratedTemplate.replace(regex, value);
    }
    return hydratedTemplate;
};

export const schemaMarkup = (schema: object) => `<script type="application/ld+json">${JSON.stringify(schema)}</script>`;

interface ContentGenerationParams {
    count: number;
    builderData: BuilderCMSResponse;
    skill: SkillInfo;
}

export interface GetTemplateParams extends ContentGenerationParams {
    key: string;
}

export const getTemplate = ({ builderData, skill, count, key }: GetTemplateParams): string => {
    const specificTemplate = builderData?.skillTemplateData?.[key];
    const genericTemplate = builderData?.genericTemplateData?.[key];
    const templateForHydration = sanitizeHTML(specificTemplate || '').length ? specificTemplate : genericTemplate;

    const variables = {
        skill_name_plural: skill.translated?.professionAliasPlural || '',
        count: count.toString(),
        skill_name: skill.translated?.name || '',
        skill_name_singular: skill.translated?.professionAlias || '',
        ...getDataInfo(),
    };

    return hydrateTemplate({ template: templateForHydration || '', variables }) || '';
};

export const getHeroBannerTitle = (params: ContentGenerationParams): string => getTemplate({ ...params, key: 'h1' });

export const getHeroBannerDescription = (params: ContentGenerationParams): string =>
    getTemplate({ ...params, key: 'tagline' });

export const getContent = (params: ContentGenerationParams): string => getTemplate({ ...params, key: 'content' });

export const getRelatedSkills = (params: Pick<ContentGenerationParams, 'builderData'>): RelatedSkill[] =>
    params.builderData.skillTemplateData?.relatedSkills || [];

export const getPageTitle = (params: ContentGenerationParams): string => getTemplate({ ...params, key: 'title' });

export const getPageDescription = (params: ContentGenerationParams): string =>
    getTemplate({ ...params, key: 'description' });

/**
 * Remove all HTML tags from a string
 * @param text
 */
export const sanitizeHTML = (text: string): string => {
    if (!text || !text.length) {
        if (!!text && typeof text !== 'string') {
            logger.warn(`sanitizeHTML: text is not a string: ${JSON.stringify(text)}`);
        }

        return '';
    }

    return text.replace(/<[^>]*>?/gm, '');
};
